import React, { useEffect, useMemo, useState } from "react";

import Constants from "../../constants";
import { getErrorMessage } from "../../common";
import courseBlocksService from "../../services/v2/courseBlocks.service";
import { BLOCK_KIND, CompositeBlockScheduleDTO, CompositeCourseDTO } from "../../models/CourseBlockDTO";
import { BlockKindPill, seTomaEn } from "../../navigation/management/courseBlocksPrompt";
import { horarioBonito } from "../../utils/horarioBonito";
import { montoBonito } from "../../utils/monto";
import { formatLocalDateTime } from "./format";

export type CompositeBlock = CompositeCourseDTO["blocks"][number];

/** What the enrollment needs: the course that bills, the block where the student starts and its real schedule. */
export interface CompositeSelection {
	course: CompositeCourseDTO;
	block: CompositeBlock;
	schedule: CompositeBlockScheduleDTO;
}

export interface CompositeCoursePickerProps {
	campusId: number;
	value: CompositeSelection | null;
	/** Fires with null while the three steps are not complete. */
	onChange: (selection: CompositeSelection | null) => void;
	disabled?: boolean;
	idPrefix?: string;
}

/** A student can START in a required or elective block; parallel and optional ones are assigned later from the ficha. */
export const puedeEmpezarEn = (block: CompositeBlock): boolean =>
	block.kindId === BLOCK_KIND.REQUIRED || block.kindId === BLOCK_KIND.ELECTIVE;

/** "3 bloques · plan: 12 meses · $2,500.00" */
export const resumenCurso = (course: CompositeCourseDTO): string => {
	const partes = [Constants.Common.blocksCount.replace("{count}", String(course.blocks?.length ?? 0))];
	if (course.billing) {
		partes.push(Constants.Common.planMonths.replace("{count}", String(course.billing.billableMonths)));
		partes.push("$" + montoBonito(course.billing.monthlyPrice));
	} else {
		partes.push(Constants.Common.planUnconfirmed);
	}
	return partes.join(" · ");
};

/** "Domingo · 11:00 am – 3:00 pm · Septiembre 2026 · Juan Pérez · 8/12" */
export const resumenHorario = (schedule: CompositeBlockScheduleDTO): string =>
	[horarioBonito(schedule.name), schedule.periodName, schedule.mentorName || Constants.Common.noMentor, `${schedule.enrolled}/${schedule.limit}`]
		.filter((parte) => parte && String(parte).trim() !== "")
		.join(" · ");

const tarjeta = (activa: boolean, deshabilitada: boolean): React.CSSProperties => ({
	border: `1px solid ${activa ? "#F45030" : "#E9E1DA"}`,
	background: activa ? "#FFF4F0" : "#FFFFFF",
	borderRadius: 6, padding: "8px 10px", cursor: deshabilitada ? "default" : "pointer",
	opacity: deshabilitada ? 0.55 : 1, display: "flex", gap: 8, alignItems: "flex-start"
});

const paso: React.CSSProperties = { fontWeight: 600, fontSize: 13, color: "#2B1C16", margin: "12px 0 6px" };
const secundario: React.CSSProperties = { fontSize: 13, color: "#6B5F58" };

/**
 * Matrícula en curso compuesto (Esteban, 2026-09-18): the enrollment belongs to the composite course (Hotelería,
 * which is what bills) but sits in a real schedule of the block the student starts with (Gastronomía 1 · Domingo).
 * Three steps: course → starting block → schedule of that block.
 */
const CompositeCoursePicker: React.FC<CompositeCoursePickerProps> = ({ campusId, value, onChange, disabled, idPrefix }) => {
	const prefix = idPrefix || "composite";
	const [courses, setCourses] = useState<CompositeCourseDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [courseId, setCourseId] = useState<number | null>(value?.course.id ?? null);
	const [blockId, setBlockId] = useState<number | null>(value?.block.id ?? null);
	const [scheduleId, setScheduleId] = useState<number | null>(value?.schedule.id ?? null);

	useEffect(() => {
		let cancelled = false;
		courseBlocksService.getCompositeCourses(campusId)
			.then((response) => { if (!cancelled) { setCourses(response.data || []); } })
			.catch((e) => { if (!cancelled) { setCourses([]); setError(getErrorMessage(e)); } });
		return () => { cancelled = true; };
	}, [campusId]);

	const course = useMemo(() => (courses || []).find((c) => c.id === courseId) ?? null, [courses, courseId]);
	const startBlocks = useMemo(() => (course?.blocks || []).filter(puedeEmpezarEn), [course]);
	const block = useMemo(() => startBlocks.find((b) => b.id === blockId) ?? null, [startBlocks, blockId]);
	const schedule = useMemo(() => (block?.schedules || []).find((s) => s.id === scheduleId) ?? null, [block, scheduleId]);

	useEffect(() => {
		onChange(course && block && schedule ? { course, block, schedule } : null);
	}, [course, block, schedule]);

	const pickCourse = (c: CompositeCourseDTO) => {
		if (disabled) return;
		setCourseId(c.id);
		setBlockId(null);
		setScheduleId(null);
	};

	const pickBlock = (b: CompositeBlock) => {
		if (disabled || (b.schedules || []).length < 1) return;
		setBlockId(b.id);
		setScheduleId(null);
	};

	const pickSchedule = (s: CompositeBlockScheduleDTO) => {
		if (disabled) return;
		setScheduleId(s.id);
	};

	if (courses === null) {
		return <div className="loader" />;
	}

	return (
		<div>
			{error ? <div style={{ fontSize: 13, color: "#A8730A", marginBottom: 6 }}>{error}</div> : ""}
			{courses.length < 1 ? <div style={{ padding: 15, color: "#6B5F58" }}>{Constants.Common.noCompositeCourses}</div> : (
				<>
					<div style={paso}>1 · {Constants.Common.chooseCompositeCourse}</div>
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
						{courses.map((c) => (
							<div key={`${prefix}_c_${c.id}`} style={tarjeta(c.id === courseId, !!disabled)} onClick={() => pickCourse(c)}>
								<input type="radio" name={`${prefix}_course`} checked={c.id === courseId} onChange={() => pickCourse(c)} disabled={disabled} style={{ marginTop: 3 }} />
								<div style={{ minWidth: 0 }}>
									<b>{c.name}</b>{c.code ? <span style={secundario}> · {c.code}</span> : ""}
									<div style={{ ...secundario, color: c.billing && !c.billing.confirmed ? "#A8730A" : "#6B5F58" }}>{resumenCurso(c)}</div>
								</div>
							</div>
						))}
					</div>

					{course ? (
						<>
							<div style={paso}>2 · {Constants.Common.startingBlock}</div>
							<div style={{ ...secundario, marginBottom: 6 }}>{Constants.Common.startingBlockHint}</div>
							{startBlocks.length < 1 ? <div style={secundario}>{Constants.Common.noStartingBlocks}</div> : (
								<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
									{startBlocks.map((b) => {
										const sinHorarios = (b.schedules || []).length < 1;
										return (
											<div key={`${prefix}_b_${b.id}`} style={tarjeta(b.id === blockId, !!disabled || sinHorarios)} onClick={() => pickBlock(b)}>
												<input type="radio" name={`${prefix}_block`} checked={b.id === blockId} onChange={() => pickBlock(b)} disabled={disabled || sinHorarios} style={{ marginTop: 3 }} />
												<div style={{ minWidth: 0, flex: 1 }}>
													<b>{b.name}</b>&nbsp;<BlockKindPill kindId={b.kindId} label={b.kindLabel} />
													<div style={secundario}>
														{Constants.Common.blockTakenIn}: {seTomaEn(b)} · {b.sessions} {Constants.Common.blockSessions.toLowerCase()}
														{sinHorarios ? <span style={{ color: "#A8730A" }}> · {Constants.Common.noBlockSchedules}</span> : ""}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							)}
						</>
					) : ""}

					{block ? (
						<>
							<div style={paso}>3 · {Constants.Common.blockScheduleHint}</div>
							<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
								{(block.schedules || []).map((s) => {
									const lleno = s.limit > 0 && s.enrolled >= s.limit;
									return (
										<div key={`${prefix}_s_${s.id}`} style={tarjeta(s.id === scheduleId, !!disabled)} onClick={() => pickSchedule(s)}>
											<input type="radio" name={`${prefix}_schedule`} checked={s.id === scheduleId} onChange={() => pickSchedule(s)} disabled={disabled} style={{ marginTop: 3 }} />
											<div style={{ minWidth: 0, flex: 1 }}>
												<span>{resumenHorario(s)}</span>
												{lleno ? <span style={{ fontSize: 12, color: "#A8730A" }}> · {Constants.Common.fullSchedule}</span> : ""}
												<div style={secundario}>
													{Constants.Common.startsOn} {formatLocalDateTime(s.startDate)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</>
					) : ""}
				</>
			)}
		</div>
	);
};

export default CompositeCoursePicker;
